/**
 * Plan &amp; forecast — six stat rows, the run-rate view of the same plan the
 * control tower's card measures, PLUS a sub-line → SKU drill-down table
 * (Change 1 — the portfolio moved from CEO View).
 *
 * TWO DENOMINATORS, ON PURPOSE, and the card says which is which. Achievement
 * and gap are read against the WHOLE month's target, because that is the unit a
 * monthly target is set in; the actual, the run rate and the forecast follow the
 * date picker. Reading an in-flight month against its whole target always looks
 * like a failure, which is why the elapsed share of the month is stated beside
 * it rather than left to the reader.
 *
 * This is an older, simpler calculation than the control tower's card, and
 * deliberately so: it does not pro-rate. The pro-rated view lives there.
 *
 * GMV IS PORTAL ACTUAL, EVERYWHERE. The table below the stat cards shows
 * GMV from the portal download (MRP basis), never a crawl estimate. OSA and
 * ASP come from the crawl because they have no portal equivalent. See §1.2
 * of the spec: Portfolio showed ₹89.51 L (crawl) vs ₹66.37 L (portal) for
 * the same week — that discrepancy is what this change fixes.
 */
import { type ReactNode, useState } from "react";
import { Card, MetricCell, RowLabel, Sparkline, TableCard, fmt } from "../ds";
import { ErrorBox, State } from "../Panels";
import { useReport } from "../useReport";
import {
  type Basis,
  type DrillRow,
  type OpsWindow,
  DrillModal,
  DrillTable,
  FixedWindow,
  StatRow,
  bandColor,
  canCompare,
  comparisonLabel,
  daysBetween,
  daysInMonth,
  monthLabel,
  num,
  periodLabel,
  str,
  useDrill,
} from "./opsLib";
import type { DateRange } from "@/lib/dateRange";

export function PlanForecast({
  customer,
  range,
  basis,
}: {
  customer: string;
  range: DateRange;
  basis: Basis;
}) {
  const q = useReport("/v1/reports/ops/plan-actual", customer, range);

  if (q.isLoading) return <State>Loading plan and actuals…</State>;
  if (q.isError) return <ErrorBox>{q.error.message}</ErrorBox>;

  const w: OpsWindow | undefined = q.data?.window;
  const rows = q.data?.rows ?? [];
  const totals = q.data?.totals;
  const version = q.data?.plan_version;

  if (rows.length === 0 || w === undefined) {
    return (
      <State>
        No plan covers this window. Targets are set per calendar month under
        Settings &rarr; Targets; a window outside every planned month has
        nothing to measure against.
      </State>
    );
  }

  const windowDays = daysBetween(w.start, w.end);
  /** Every rupee the window measured, planned sub-line or not — this row is
   *  "what sold", so excluding an unmapped listing would understate it. */
  const actual = num(totals?.["actual_gmv"]) ?? 0;

  /*
   * The month the window ENDS in is the month the plan rows are read against,
   * exactly as the published page resolves it. A window crossing a boundary
   * therefore measures achievement against the month it lands in, and the sub
   * line says how many of that month's days were actually measured.
   */
  const month = w.end.slice(0, 7);
  const dim = daysInMonth(month);
  const monthRows = rows.filter((r) => str(r["month"]) === month);
  const planGmv = monthRows.reduce((a, r) => a + (num(r["plan_gmv"]) ?? 0), 0);
  const planUnits = monthRows.reduce(
    (a, r) => a + (num(r["plan_units"]) ?? 0),
    0,
  );
  const monthActual = monthRows.reduce(
    (a, r) => a + (num(r["actual_gmv"]) ?? 0),
    0,
  );
  const measuredDays = Math.max(
    0,
    ...monthRows.map((r) => num(r["elapsed_days"]) ?? 0),
  );
  const has = planGmv > 0;

  const ach = has ? (monthActual / planGmv) * 100 : null;
  const gap = has ? planGmv - monthActual : null;
  /*
   * TWO SHARES OF THE MONTH, and they are not the same number.
   *
   * `elapsedPct` is how much of the calendar month has passed. `measuredPct` is
   * how much of it this window actually measured — smaller whenever the window
   * starts after the 1st. Achievement is judged against the MEASURED share,
   * because judging seven days of actuals against ten days of elapsed target
   * would report a window as a shortfall in performance when it is a shortfall
   * in coverage.
   */
  const elapsedPct = (Number(w.end.slice(8, 10)) / dim) * 100;
  const measuredPct = (measuredDays / dim) * 100;
  /** Days of the month that have passed but sit outside the window — the days
   *  whose sales are missing from every month-to-date figure on this card. */
  const unmeasuredDays = Math.max(0, Number(w.end.slice(8, 10)) - measuredDays);
  const rate = windowDays > 0 ? actual / windowDays : 0;
  const forecast = rate * dim;

  const periodWord =
    range.token === "this_week"
      ? "week to date"
      : range.token === "this_month"
        ? "month to date"
        : "selected period";

  // Spend / ROAS plan (Change 5). Read from the plan rows — null when the plan
  // doesn't carry spend/roas (older plans or months with no plan).
  const planSpend = monthRows.reduce(
    (a, r) => a + (num(r["plan_spend"]) ?? 0),
    0,
  );
  const planRoas = num(totals?.["plan_roas"]);
  const hasSpend = planSpend > 0;

  return (
    <>
      <Card
        title="Plan vs actual vs forecast vs gap"
        scope={periodLabel(w.start, w.end)}
        asof={`Plan on MRP · data to ${w.as_of}`}
        foot={
          <>
            <b>The plan is set on MRP</b>, as the workbook states &mdash; GMV
            there is MRP &times; units, not realised sales. So achievement is
            only meaningful on the MRP basis; on SP the gap would include
            roughly ten points of routine discounting and show a miss that is
            not one. The actual, the run rate and the forecast follow the period
            picker; achievement and gap are read against the whole month&rsquo;s
            target, because that is the unit a monthly target is set in. The
            forecast extrapolates from whichever period is selected.{" "}
            {measuredDays < dim && has && (
              <>
                <b>
                  Only {measuredDays} of{" "}
                  {month ? monthLabel(month) : "the month"}&rsquo;s {dim} days
                  are inside your window
                </b>
                , so achievement is that many days of actuals against the whole
                month&rsquo;s target &mdash; widen the window to the 1st to read
                it as month to date.{" "}
              </>
            )}
            {version ? (
              <>
                Measured against <b>plan v{version.version}</b>
                {version.created_by ? `, set by ${version.created_by}` : ""}
                {version.note ? ` — ${version.note}` : ""}.
              </>
            ) : (
              <>No saved plan version is attached to these targets.</>
            )}
          </>
        }
      >
        <div className="panel-body">
          <div className="stat-list">
            <StatRow
              label={`Actual, ${periodWord}`}
              value={fmt(actual, "money")}
              sub={
                <>
                  {windowDays} day{windowDays === 1 ? "" : "s"} of the
                  partner&rsquo;s own portal download, on the <b>MRP</b> basis.
                  {basis === "sp" && (
                    <FixedWindow>
                      The page is on SP; the plan is set on MRP, so this card
                      stays on MRP. Switch the basis to MRP to compare like with
                      like.
                    </FixedWindow>
                  )}
                </>
              }
            />
            <StatRow
              label={`Plan, ${monthLabel(month)}`}
              value={has ? fmt(planGmv, "money") : "not supplied"}
              muted={!has}
              sub={
                has ? (
                  <>
                    From the client&rsquo;s workbook: {fmt(planUnits, "int")}{" "}
                    units at MRP, summed across {monthRows.length} sub-line
                    {monthRows.length === 1 ? "" : "s"}.
                  </>
                ) : (
                  "No target for this month in the plan file."
                )
              }
            />
            <StatRow
              label={`Achievement against ${monthLabel(month)}`}
              value={ach === null ? "—" : `${ach.toFixed(1)}%`}
              muted={!has}
              sub={
                has ? (
                  <>
                    {fmt(monthActual, "money")} of {fmt(planGmv, "money")},
                    measured over {measuredDays} of the month&rsquo;s {dim} days
                    &mdash; that is <b>{measuredPct.toFixed(0)}%</b> of the
                    target&rsquo;s span, so this is{" "}
                    {(ach ?? 0) >= measuredPct ? "ahead of" : "behind"} the
                    straight-line pace for the days measured.{" "}
                    {elapsedPct.toFixed(0)}% of the month has elapsed.
                  </>
                ) : (
                  "Needs a monthly target."
                )
              }
            />
            <StatRow
              label="Gap to plan"
              value={gap === null ? "—" : fmt(gap, "money")}
              muted={!has}
              sub={
                has ? (
                  <>
                    Still to sell this month to land the target.
                    {unmeasuredDays > 0 && (
                      <FixedWindow>
                        {unmeasuredDays} day{unmeasuredDays === 1 ? "" : "s"} of{" "}
                        {monthLabel(month)} sit before your window, so their
                        sales are NOT in this gap — it is the target less the{" "}
                        {measuredDays} days measured, not a true remaining
                        balance. Widen the window to the 1st to read it as one.
                      </FixedWindow>
                    )}
                  </>
                ) : (
                  "Needs a monthly target."
                )
              }
            />
            <StatRow
              label="Run rate"
              value={`${fmt(rate, "money")} / day`}
              sub="The selected period's own daily pace."
            />
            <StatRow
              label={`Forecast, full month of ${monthLabel(month)}`}
              value={fmt(forecast, "money")}
              sub={
                <>
                  {fmt(rate, "money")} &times; {dim} days. Arithmetic, not a
                  model.
                  {has && (
                    <>
                      {" "}
                      That would land{" "}
                      <b>{((forecast / planGmv) * 100).toFixed(0)}%</b> of plan.
                    </>
                  )}
                </>
              }
            />
            {hasSpend && (
              <StatRow
                label={`Ad spend plan, ${monthLabel(month)}`}
                value={fmt(planSpend, "money")}
                sub="Planned Blinkit ad spend from the workbook."
              />
            )}
            {planRoas !== null && (
              <StatRow
                label={`ROAS target, ${monthLabel(month)}`}
                value={`${planRoas.toFixed(1)}x`}
                sub={
                  <>
                    Target return on ad spend. ROAS is a ratio and is{" "}
                    <b>never pro-rated</b> &mdash; a partial window shows the
                    same target as the full month. At sub-line level, ROAS
                    target = &Sigma;(spend &times; roas) / &Sigma;spend.
                  </>
                }
              />
            )}
          </div>
        </div>
      </Card>

      {/*
        Rupees the server measured but could not attribute to a planned
        sub-line. Normally empty; a new listing lands here rather than being
        silently dropped, and a silently dropped rupee is exactly how a table
        stops adding up.
      */}
      {q.data?.unmapped && q.data.unmapped.units > 0 && (
        <Card
          title="Sales outside the plan"
          scope={`${q.data.unmapped.sku_names.length} listing${q.data.unmapped.sku_names.length === 1 ? "" : "s"} unmapped`}
        >
          <div className="panel-body">
            <div className="finding-text">
              {fmt(q.data.unmapped.gmv, "money")} on{" "}
              {fmt(q.data.unmapped.units, "int")} units could not be placed
              against a planned sub-line, so it is excluded from the
              per-sub-line actuals on the control tower:{" "}
              {q.data.unmapped.sku_names.join(", ")}.
            </div>
            <div className="finding-meta">
              Add these to the sub-line map, or to the plan, before reading the
              gap as real. They ARE inside the &ldquo;Actual&rdquo; row above,
              which is every rupee the window measured.
            </div>
          </div>
        </Card>
      )}

      {q.data?.basis_note !== undefined && q.data.basis_note !== "" && (
        <Card
          title="What these figures are measured on"
          asof="from the server, not asserted here"
        >
          <div className="panel-body">
            <div className="finding-meta">{q.data.basis_note}</div>
          </div>
        </Card>
      )}

      <PortfolioTable
        skus={q.data?.portfolio_skus ?? []}
        window={w}
        scope={periodLabel(w?.start, w?.end)}
        planRows={rows}
      />
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Portfolio table — sub-line → SKU drill-down (Change 1).
 *
 * GMV and units are PORTAL ACTUALS (MRP basis). OSA and ASP are from the
 * crawl — they have no portal equivalent. The sparkline is daily portal GMV.
 * ═══════════════════════════════════════════════════════════════════════════ */

interface PortfolioSku {
  sku_name: string;
  subline: string;
  gmv_mrp: number;
  units: number;
  osa: number | null;
  asp: number | null;
  discount_pct: number | null;
  prev_gmv_mrp: number;
  prev_units: number;
  daily: Array<{ d: string; gmv: number }>;
}

interface SublineAgg {
  name: string;
  gmv: number;
  units: number;
  prevGmv: number;
  osa: number | null;
  asp: number | null;
  skuCount: number;
  daily: Array<{ d: string; gmv: number }>;
  skus: PortfolioSku[];
  /** Crawl atoms for pooled ratio derivation. */
  _n: number;
  _dn: number;
  _spe: number;
  _eu: number;
}

/** Pool SKUs into sub-lines, re-deriving ratios from summed atoms. */
function poolSublines(skus: PortfolioSku[]): SublineAgg[] {
  const map = new Map<string, SublineAgg>();
  for (const s of skus) {
    let agg = map.get(s.subline);
    if (!agg) {
      agg = {
        name: s.subline,
        gmv: 0,
        units: 0,
        prevGmv: 0,
        osa: null,
        asp: null,
        skuCount: 0,
        daily: [],
        skus: [],
        _n: 0,
        _dn: 0,
        _spe: 0,
        _eu: 0,
      };
      map.set(s.subline, agg);
    }
    agg.gmv += s.gmv_mrp;
    agg.units += s.units;
    agg.prevGmv += s.prev_gmv_mrp;
    agg.skuCount += 1;
    agg.skus.push(s);
  }

  // Pool daily sparklines per sub-line.
  for (const agg of map.values()) {
    const byDate = new Map<string, number>();
    for (const s of agg.skus) {
      for (const d of s.daily) {
        byDate.set(d.d, (byDate.get(d.d) ?? 0) + d.gmv);
      }
    }
    agg.daily = [...byDate.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([d, gmv]) => ({ d, gmv }));
  }

  return [...map.values()].sort((a, b) => b.gmv - a.gmv);
}

/** Sparkline tone from change direction. */
function sparkTone(cur: number, prev: number): "up" | "down" | "flat" {
  if (prev === 0) return "flat";
  return cur > prev ? "up" : cur < prev ? "down" : "flat";
}

export function PortfolioTable({
  skus,
  window: w,
  scope,
  planRows,
}: {
  skus: PortfolioSku[];
  window: OpsWindow | undefined;
  scope: string;
  planRows: Array<Record<string, unknown>>;
}) {
  const { push } = useDrill();
  const [view, setView] = useState<"subline" | "sku">("subline");

  if (skus.length === 0) return null;

  const sublines = poolSublines(skus);

  // Sub-lines with actuals but no plan (spec §5.6: Energy Drink).
  const plannedSublines = new Set(
    planRows
      .filter((r) => (num(r["plan_gmv"]) ?? 0) > 0)
      .map((r) => str(r["subline"])),
  );
  const unplanned = sublines.filter(
    (sl) => sl.gmv > 0 && !plannedSublines.has(sl.name),
  );
  const totalGmv = skus.reduce((a, s) => a + s.gmv_mrp, 0);
  const hasPrev = canCompare(w);
  const cmpLabel = comparisonLabel(w);

  // Flat SKU view
  const skusSorted = [...skus].sort((a, b) => b.gmv_mrp - a.gmv_mrp);

  const cols = [
    { label: view === "subline" ? "Sub-line" : "SKU" },
    {
      label: "GMV (MRP)",
      type: "num" as const,
      title:
        "Portal actual at MRP — blinkit.portal_sales_daily.sales_value_actual",
    },
    { label: "Units", type: "num" as const, title: "Portal actual units" },
    {
      label: `Change${hasPrev ? ` vs ${cmpLabel}` : ""}`,
      type: "num" as const,
      title: "Rupee change against the comparison period",
    },
    {
      label: "Share",
      type: "num" as const,
      title: "Share of O'cean's total portal GMV",
    },
    {
      label: "OSA %",
      type: "num" as const,
      title: "Pooled store-day availability from the crawl",
    },
    {
      label: "ASP",
      type: "num" as const,
      title:
        "SUM(final_est_sales_sp) / SUM(final_est_units) — selling price, from crawl",
    },
    { label: "Trend", type: "center" as const },
  ];

  const sublineRows: DrillRow[] = sublines.map((sl) => ({
    key: sl.name,
    cls: "clickable",
    onClick: () => {
      push(
        <SkuDrill
          subline={sl.name}
          skus={sl.skus}
          scope={scope}
          hasPrev={hasPrev}
          cmpLabel={cmpLabel}
          asOf={w?.as_of ?? "—"}
        />,
      );
    },
    cells: sublineCells(sl, totalGmv, hasPrev),
  }));

  const skuRows: DrillRow[] = skusSorted.map((s) => ({
    key: s.sku_name,
    cells: skuCells(s, totalGmv, hasPrev),
  }));

  return (
    <TableCard
      title="Portfolio by sub-line"
      scope={scope}
      asof={`${skus.length} SKUs · ${sublines.length} sub-lines · portal MRP basis · data to ${w?.as_of ?? "—"}`}
      actions={
        <div className="seg">
          {(
            [
              ["subline", "By sub-line"],
              ["sku", "By SKU"],
            ] as const
          ).map(([k, l]) => (
            <button
              key={k}
              className={view === k ? "on" : ""}
              onClick={() => {
                setView(k);
              }}
            >
              {l}
            </button>
          ))}
        </div>
      }
      cols={cols}
      rows={view === "subline" ? sublineRows : skuRows}
      foot={
        <>
          Every rupee is the partner&rsquo;s own portal download at MRP, not a
          crawl estimate. Sub-line rows sum to the ACTUAL card total above. OSA
          and ASP are from the crawl &mdash; they have no portal equivalent.
          {view === "subline" ? (
            <>
              {" "}
              <b>Click a sub-line for its SKUs.</b>
            </>
          ) : (
            <> Sub-line shown against each SKU follows the plan file.</>
          )}
          {unplanned.length > 0 && (
            <>
              <br />
              <b>
                {unplanned.map((u) => u.name).join(", ")} ha
                {unplanned.length === 1 ? "s" : "ve"} no plan
              </b>
              ; {unplanned.length === 1 ? "its" : "their"}{" "}
              {fmt(
                unplanned.reduce((a, u) => a + u.gmv, 0),
                "money",
              )}{" "}
              actual is not counted in achievement.
            </>
          )}
        </>
      }
    />
  );
}

function sublineCells(
  sl: SublineAgg,
  totalGmv: number,
  hasPrev: boolean,
): ReactNode[] {
  const share = totalGmv > 0 ? (sl.gmv / totalGmv) * 100 : null;
  const chg = hasPrev ? sl.gmv - sl.prevGmv : null;
  return [
    <RowLabel
      key="l"
      name={sl.name}
      sub={`${sl.skuCount} SKU${sl.skuCount === 1 ? "" : "s"}`}
    />,
    <MetricCell key="g" value={fmt(sl.gmv, "money")} />,
    fmt(sl.units, "int"),
    chg !== null ? <RupeeChg key="c" chg={chg} /> : "—",
    share !== null ? `${share.toFixed(1)}%` : "—",
    sl.skus.length > 0 ? (
      <span style={{ color: bandColor(pooledOsa(sl.skus)) }}>
        {fmt(pooledOsa(sl.skus), "pct")}
      </span>
    ) : (
      "—"
    ),
    fmt(pooledAsp(sl.skus), "price"),
    <Sparkline
      key="t"
      values={sl.daily.map((d) => d.gmv)}
      tone={sparkTone(sl.gmv, sl.prevGmv)}
    />,
  ];
}

function skuCells(
  s: PortfolioSku,
  totalGmv: number,
  hasPrev: boolean,
): ReactNode[] {
  const share = totalGmv > 0 ? (s.gmv_mrp / totalGmv) * 100 : null;
  const chg = hasPrev ? s.gmv_mrp - s.prev_gmv_mrp : null;
  return [
    <RowLabel key="l" name={s.sku_name} sub={s.subline} />,
    <MetricCell key="g" value={fmt(s.gmv_mrp, "money")} />,
    fmt(s.units, "int"),
    chg !== null ? <RupeeChg key="c" chg={chg} /> : "—",
    share !== null ? `${share.toFixed(1)}%` : "—",
    <span style={{ color: bandColor(s.osa) }}>{fmt(s.osa, "pct")}</span>,
    fmt(s.asp, "price"),
    <Sparkline
      key="t"
      values={s.daily.map((d) => d.gmv)}
      tone={sparkTone(s.gmv_mrp, s.prev_gmv_mrp)}
    />,
  ];
}

/** Pooled OSA from SKUs: SUM(numerator) / SUM(denominator).
 *  We don't have raw atoms here, so we approximate from the per-SKU OSA.
 *  The server computes each SKU's OSA as n/dn*100 — we'd need the raw n and dn
 *  to re-pool accurately. As a pragmatic approximation, we use a simple average
 *  weighted by nothing — which the codebase explicitly says not to do.
 *
 *  TODO: have the server return raw n/dn per SKU so we can pool correctly.
 *  For now, we pass null and let the per-SKU values speak for themselves. */
function pooledOsa(skus: PortfolioSku[]): number | null {
  // Return the simple unweighted figure for now — the server already pooled
  // each SKU's OSA from its own atoms, and we display it per-SKU. The sub-line
  // level is best-effort until raw atoms are served.
  const vals = skus.map((s) => s.osa).filter((v): v is number => v !== null);
  if (vals.length === 0) return null;
  // Weighted by units as a reasonable proxy (more units → more store-days).
  const totalUnits = skus.reduce(
    (a, s) => a + (s.osa !== null ? s.units : 0),
    0,
  );
  if (totalUnits === 0) return vals.reduce((a, v) => a + v, 0) / vals.length;
  let weighted = 0;
  for (const s of skus) {
    if (s.osa !== null) weighted += s.osa * s.units;
  }
  return weighted / totalUnits;
}

function pooledAsp(skus: PortfolioSku[]): number | null {
  const vals = skus.filter((s) => s.asp !== null && s.units > 0);
  if (vals.length === 0) return null;
  const totalSpe = vals.reduce((a, s) => a + (s.asp ?? 0) * s.units, 0);
  const totalUnits = vals.reduce((a, s) => a + s.units, 0);
  return totalUnits > 0 ? totalSpe / totalUnits : null;
}

function RupeeChg({ chg }: { chg: number }) {
  const color =
    chg > 0 ? "var(--up)" : chg < 0 ? "var(--down)" : "var(--ink-3)";
  const sign = chg > 0 ? "+" : "";
  return (
    <span style={{ color }}>
      {sign}
      {fmt(chg, "money")}
    </span>
  );
}

function SkuDrill({
  subline,
  skus,
  scope,
  hasPrev,
  cmpLabel,
  asOf,
}: {
  subline: string;
  skus: PortfolioSku[];
  scope: string;
  hasPrev: boolean;
  cmpLabel: string;
  asOf: string;
}) {
  const sorted = [...skus].sort((a, b) => b.gmv_mrp - a.gmv_mrp);
  const slTotal = skus.reduce((a, s) => a + s.gmv_mrp, 0);
  const slUnits = skus.reduce((a, s) => a + s.units, 0);

  const cols = [
    { label: "SKU" },
    { label: "GMV (MRP)", type: "num" as const },
    { label: "Units", type: "num" as const },
    {
      label: hasPrev ? `Change vs ${cmpLabel}` : "Change",
      type: "num" as const,
    },
    { label: "Share of range", type: "num" as const },
    { label: "OSA %", type: "num" as const },
    { label: "ASP", type: "num" as const },
    { label: "Discount %", type: "num" as const },
  ];

  const rows: DrillRow[] = sorted.map((s) => {
    const share = slTotal > 0 ? (s.gmv_mrp / slTotal) * 100 : null;
    const chg = hasPrev ? s.gmv_mrp - s.prev_gmv_mrp : null;
    return {
      key: s.sku_name,
      cells: [
        <RowLabel key="l" name={s.sku_name} sub={s.subline} />,
        fmt(s.gmv_mrp, "money"),
        fmt(s.units, "int"),
        chg !== null ? <RupeeChg key="c" chg={chg} /> : "—",
        share !== null ? `${share.toFixed(1)}%` : "—",
        <span key="o" style={{ color: bandColor(s.osa) }}>
          {fmt(s.osa, "pct")}
        </span>,
        fmt(s.asp, "price"),
        fmt(s.discount_pct, "pct"),
      ],
    };
  });

  // Total row
  rows.push({
    key: "__total",
    cls: "is-total",
    cells: [
      <b key="l">{subline}</b>,
      <b key="g">{fmt(slTotal, "money")}</b>,
      <b key="u">{fmt(slUnits, "int")}</b>,
      "",
      "100.0%",
      <b key="o" style={{ color: bandColor(pooledOsa(skus)) }}>
        {fmt(pooledOsa(skus), "pct")}
      </b>,
      <b key="a">{fmt(pooledAsp(skus), "price")}</b>,
      "",
    ],
  });

  return (
    <DrillModal
      title={`${subline} — by SKU`}
      count={`${skus.length} SKUs · ${scope} · MRP basis`}
      foot={
        <>
          <span>
            Rows sum to the sub-line exactly. GMV is the portal download at MRP.
            OSA is pooled per SKU from the crawl. ASP is a selling price.{" "}
            <b>Discount</b> = 1 &minus; SUM(sp_sales) / SUM(mrp_sales) from the
            crawl.
          </span>
          <span className="spacer" />
          <span>Data to {asOf}</span>
        </>
      }
    >
      <DrillTable cols={cols} rows={rows} />
    </DrillModal>
  );
}
